import React, { useState, useEffect, useRef, useContext } from 'react';
import { Send } from 'lucide-react';
import styles from './LiveChatModule.module.css';

import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getDatabase,
  ref,
  push,
  onValue,
  serverTimestamp,
  query,
  orderByChild,
  limitToLast,
} from 'firebase/database';
import { SwapMainAppContext } from '../../context/SwapMainAppContext';
import { shortenAddress, useIsMobile } from '../../utils/utils';
// import { PRISMAX_BACKEND_URL } from '../../configs/config';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

if (process.env.REACT_APP_FIREBASE_MEASUREMENT_ID) {
  firebaseConfig.measurementId = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID;
}

const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.databaseURL &&
    firebaseConfig.projectId &&
    firebaseConfig.storageBucket &&
    firebaseConfig.messagingSenderId &&
    firebaseConfig.appId
);

if (!isFirebaseConfigured) {
  console.warn(
    'Live chat: Firebase env vars missing (REACT_APP_FIREBASE_*). Add them to .env.local — see Firebase console Project settings.'
  );
}

let firebaseDatabase = null;

function getFirebaseDatabase() {
  if (!isFirebaseConfigured) {
    return null;
  }
  if (!firebaseDatabase) {
    const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    firebaseDatabase = getDatabase(app);
  }
  return firebaseDatabase;
}

export default function LiveChat({ robotId, maxMessages = 10, onTypingChange }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const chatRoomRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const isMobile = useIsMobile(1000);

  const {
    showNotification,
    userEmail,
    walletAddress,
    userClass,
    userId,
    gatewayToken,
    setPointsRefreshTrigger,
    videoId: videoIdMap,
  } = useContext(SwapMainAppContext);

  const resolvedVideoId = videoIdMap && robotId ? (videoIdMap[robotId] || 'default-room') : 'default-room';

  const hasUserInteractedRef = useRef(false);
  const previousMessageCountRef = useRef(0);
  const [isCheckingReward, setIsCheckingReward] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);

  const scrollToBottom = () => {
    if (isMobile && !hasUserInteractedRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const isNewMessage = messages.length > previousMessageCountRef.current;
    previousMessageCountRef.current = messages.length;
    if (!isNewMessage || messages.length === 0) return;
    if (isMobile && !hasUserInteractedRef.current) return;
    if (isMobile) {
      const timer = setTimeout(scrollToBottom, 100);
      return () => clearTimeout(timer);
    } else {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    setIsLoadingMessages(true);
    const database = getFirebaseDatabase();
    if (!database) {
      setIsConnected(false);
      setIsLoadingMessages(false);
      setMessages([]);
      return undefined;
    }
    try {
      chatRoomRef.current = ref(database, `chats/${resolvedVideoId}/messages`);
      const messageLimit = Math.max(1, maxMessages);
      const limitedMessagesQuery = query(
        chatRoomRef.current,
        orderByChild('timestamp'),
        limitToLast(messageLimit)
      );
      unsubscribeRef.current = onValue(
        limitedMessagesQuery,
        (snapshot) => {
          const data = snapshot.val();
          if (data) {
            const messageList = Object.entries(data)
              .map(([key, value]) => ({ id: key, ...value, timestamp: typeof value.timestamp === 'number' ? value.timestamp : 0 }))
              .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
              .slice(-messageLimit);
            setMessages(messageList);
          } else {
            setMessages([]);
          }
          setIsConnected(true);
          setIsLoadingMessages(false);
        },
        (error) => {
          console.error('Firebase connection error:', error);
          setIsConnected(false);
          setIsLoadingMessages(false);
        }
      );
      setIsConnected(true);
    } catch (error) {
      console.error('Error setting up Firebase:', error);
      setIsConnected(false);
      setIsLoadingMessages(false);
    }

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [resolvedVideoId, maxMessages]);

  // Have commented out the reward for messages (ticket PRIS-125)
  // const checkCommentReward = async (messageText) => {
  //   if (!userId) return;
  //   try {
  //     setIsCheckingReward(true);

  //     const headers = {
  //       'Content-Type': 'application/json',
  //       ...(gatewayToken ? { Authorization: `Bearer ${gatewayToken}` } : {}),
  //     };
  //     const requestBody = {
  //       user_id: userId,
  //       message: messageText,
  //     };
  //     const response = await fetch(`${PRISMAX_BACKEND_URL}/api/check-comment-reward`, {
  //       method: 'POST',
  //       headers,
  //       body: JSON.stringify(requestBody),
  //     });
  //     if (response.ok) {
  //       const data = await response.json();
  //       if (data.success && data.points_awarded > 0) {
  //         setPointsRefreshTrigger((prev) => prev + 1);
  //         showNotification('success', `You earned ${data.points_awarded} points for your comment!`);
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error checking comment reward:', error);
  //   } finally {
  //     setIsCheckingReward(false);
  //   }
  // };

  const sendMessage = async (e) => {
    e.preventDefault();
    const messageText = inputMessage.trim();
    if (!messageText || !isConnected) return;
    if (userEmail === '' && walletAddress === '') {
      showNotification('warning', 'Please login first');
      return;
    }
    try {
      hasUserInteractedRef.current = true;
      setInputMessage('');
      await push(chatRoomRef.current, {
        username: userEmail ? shortenAddress(userEmail) : shortenAddress(walletAddress),
        message: messageText,
        timestamp: serverTimestamp(),
      });
      // Have commented out the reward for messages (ticket PRIS-125)
      // if (userId && messageText.length >= 10) {
      //   checkCommentReward(messageText);
      // }
      showNotification('success', 'Your comment has been recorded successfully!');
      if (isMobile) {
        setTimeout(scrollToBottom, 200);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setInputMessage(messageText);
    }
  };

  return (
    <>
      <div className={styles.messagesContainer} onClick={() => { if (isMobile) hasUserInteractedRef.current = true; }}>
        {!isFirebaseConfigured ? (
          <div className={styles.emptyState}>
            Live chat is off locally. Add REACT_APP_FIREBASE_* variables to .env.local (Firebase Console → Project settings → Your apps).
          </div>
        ) : !isLoadingMessages && messages.length === 0 ? (
          <div className={styles.emptyState}>No messages yet. Start the conversation!</div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={styles.messageItem}>
              <div className={styles.messageHeader}>
                <span className={styles.messageUsername}>{msg.username}</span>
                <span className={styles.messageText}>{msg.message}</span>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      <div style={{ width: '100%', height: '1px', background: '#6B6B6B', marginBottom: '24px' }} />
      <form onSubmit={sendMessage} className={styles.inputContainer}>
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => {
            setInputMessage(e.target.value);
            if (isMobile && onTypingChange) onTypingChange(e.target.value.length > 0);
          }}
          onFocus={() => { if (isMobile && onTypingChange) onTypingChange(inputMessage.length > 0); }}
          onBlur={() => { if (isMobile && onTypingChange) onTypingChange(false); }}
          placeholder="Send message to the chat..."
          className={styles.messageInput}
          data-teleop-chat-input="true"
          disabled={!isConnected}
          maxLength={200}
        />
        <button type="submit" className={styles.sendButton} disabled={!isConnected || !inputMessage.trim()}>
          <Send size={20} />
        </button>
      </form>
    </>
  );
}
